import { Capacitor } from '@capacitor/core';
import { ActionPerformed, PushNotifications, PushNotificationSchema, Token } from '@capacitor/push-notifications';
import { arrayUnion, doc, setDoc } from 'firebase/firestore';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { db } from '../firebase';

export const usePushNotifications = () => {
    const { userRole, clientId, dietitianId } = useAuth();
    const userId = userRole === 'client' ? clientId : dietitianId;
    const navigate = useNavigate();
    const { showToast } = useToast();

    useEffect(() => {
        console.log('🔔 usePushNotifications Hook Tetiklendi');
        console.log('📱 Platform:', Capacitor.getPlatform());
        console.log('👤 User Info:', { userRole, clientId, dietitianId, userId });

        // Sadece native platformlarda çalıştır (Android/iOS)
        if (!Capacitor.isNativePlatform()) {
            console.log('⚠️ Push Notifications: Native platform değil, atlanıyor.');
            return;
        }

        // Kullanıcı giriş yapmamışsa işlem yapma
        if (!userId) {
            console.log('⏳ Push Notifications: Kullanıcı ID yok, giriş bekleniyor...');
            return;
        }

        console.log('🚀 Push Notifications: Başlatılıyor... UserID:', userId);

        const register = async () => {
            try {
                console.log('🔍 İzinler kontrol ediliyor...');
                let permStatus = await PushNotifications.checkPermissions();
                console.log('📊 Mevcut izin durumu:', JSON.stringify(permStatus));

                if (permStatus.receive === 'prompt') {
                    console.log('🙏 İzin isteniyor...');
                    permStatus = await PushNotifications.requestPermissions();
                    console.log('📝 İzin isteme sonucu:', JSON.stringify(permStatus));
                }

                if (permStatus.receive !== 'granted') {
                    console.error('❌ Bildirim izni reddedildi!');
                    showToast('warning', 'Bildirim izni reddedildi. Ayarlardan açabilirsiniz.');
                    return;
                }

                console.log('✅ İzin verildi, kayıt olunuyor...');
                await PushNotifications.register();
            } catch (error) {
                console.error('💥 Push Notifications Kayıt Hatası:', error);
                showToast('error', 'Bildirim servisi başlatılamadı: ' + JSON.stringify(error));
            }
        };

        register();

        // Listener'ları ekle
        const addListeners = async () => {
            await PushNotifications.removeAllListeners();

            // Kayıt başarılı olduğunda token'ı al ve Firestore'a kaydet
            await PushNotifications.addListener('registration', async (token: Token) => {
                console.log('🎉 Push Registration Token:', token.value);
                showToast('success', 'Bildirim servisi bağlandı!');

                if (userId) {
                    try {
                        const tokenRef = doc(db, 'fcm_tokens', userId);

                        // Token'ı array olarak kaydet (birden fazla cihaz desteği için)
                        await setDoc(tokenRef, {
                            tokens: arrayUnion(token.value),
                            userRole: userRole,
                            userId: userId,
                            updatedAt: new Date().toISOString()
                        }, { merge: true });

                        console.log('💾 FCM Token Firestore\'a kaydedildi');
                    } catch (error) {
                        console.error('❌ FCM Token kaydedilirken hata:', error);
                    }
                }
            });

            await PushNotifications.addListener('registrationError', (error: any) => {
                console.error('❌ Push registration error: ' + JSON.stringify(error));
                showToast('error', 'Bildirim kaydı başarısız oldu.');
            });

            await PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
                console.log('📩 Push received: ' + JSON.stringify(notification));
                showToast('info', `Yeni Mesaj: ${notification.title || 'Bildirim'}`);
            });

            await PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
                console.log('👆 Push action performed: ' + JSON.stringify(notification));

                // Bildirime tıklandığında mesajlar sayfasına yönlendir
                const data = notification.notification.data;
                if (data && data.url) {
                    navigate(data.url);
                } else {
                    // Varsayılan olarak mesajlara git
                    if (userRole === 'client') {
                        navigate('/client/messages');
                    } else if (userRole === 'dietitian') {
                        navigate('/dietitian/messages');
                    }
                }
            });
        };

        addListeners();

        return () => {
            if (Capacitor.isNativePlatform()) {
                PushNotifications.removeAllListeners();
            }
        };

    }, [userId, userRole, navigate, showToast,clientId,dietitianId]);
};
